// src/features/curatedLists/utils/errorHandling.ts

export interface ApiError {
  detail?: string;
  message?: string;
  response?: {
    data?: {
      detail?: string;
      message?: string;
    };
  };
}

/**
 * Extract error message from various error formats
 * 
 * Handles errors from:
 * - Django REST framework (error.response.data.detail)
 * - Axios errors (error.response.data.message)
 * - Standard errors (error.message)
 * - Custom API errors (error.detail)
 * 
 * @param error - The error object from API call
 * @param fallback - Default message if no error details found
 * @returns Extracted error message
 */
export function extractErrorMessage(error: any, fallback: string): string {
  // Try Django REST framework format first
  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  // Try Axios error format
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Try direct detail property (our ApiError type)
  if (error?.detail) {
    return error.detail;
  }
  
  // Try standard error message
  if (error?.message) {
    return error.message;
  }
  
  // Fallback
  return fallback;
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: any): boolean {
  if (!error) return false;
  const message = extractErrorMessage(error, '').toLowerCase();
  return (
    message.includes('authentication') ||
    message.includes('unauthorized') ||
    message.includes('not authenticated') ||
    error?.response?.status === 401
  );
}

/**
 * Check if error is a permission error
 */
export function isPermissionError(error: any): boolean {
  if (!error) return false;
  const message = extractErrorMessage(error, '').toLowerCase();
  return (
    message.includes('permission') ||
    message.includes('forbidden') ||
    error?.response?.status === 403
  );
}

/**
 * Check if error is a not found error
 */
export function isNotFoundError(error: any): boolean {
  if (!error) return false;
  const message = extractErrorMessage(error, '').toLowerCase();
  return (
    message.includes('not found') ||
    error?.response?.status === 404
  );
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: any): boolean {
  if (!error) return false;
  const message = extractErrorMessage(error, '').toLowerCase();
  return (
    message.includes('invalid') ||
    message.includes('validation') ||
    error?.response?.status === 400
  );
}

/**
 * Get user-friendly error message based on error type
 */
export function getUserFriendlyError(error: any, context?: string): string {
  if (isAuthError(error)) {
    return 'Please log in to continue';
  }
  
  if (isPermissionError(error)) {
    return context 
      ? `You don't have permission to ${context}`
      : `You don't have permission to perform this action`;
  }
  
  if (isNotFoundError(error)) {
    return context 
      ? `${context} not found`
      : 'The requested resource was not found';
  }
  
  if (isValidationError(error)) {
    return extractErrorMessage(error, 'Invalid input. Please check your data.');
  }
  
  // Default to extracted message
  return extractErrorMessage(
    error, 
    context ? `Failed to ${context}` : 'An error occurred'
  );
}